import { get } from 'lodash-es';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

export const MEMBERSHIP_TERMS_V01 = 'v01';
export const MEMBERSHIP_TERMS_V02 = 'v02';
export const MEMBERSHIP_DISPLAY_DATE_FORMAT = 'ddd MMM D, YYYY';
export const PAUSE_DURATION_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const MANAGE_PAUSE_MOTTO =
  'At Squeeze, we like to ease tension, not create it.';

dayjs.extend(utc);

export function isMembershipTermsV02(membershipDetails) {
  return (
    get(membershipDetails, 'membership_terms_version') === MEMBERSHIP_TERMS_V02
  );
}

export function getPauseFeeAmount(membershipDetails) {
  const pauseFee = get(membershipDetails, 'pause_fee');
  if (pauseFee != null && Number(pauseFee) > 0) {
    return Number(pauseFee);
  }
  return 15;
}

export function shouldShowPauseFee(membershipDetails) {
  if (get(membershipDetails, 'pause_fee_waived')) {
    return false;
  }
  return true;
}

export function getPauseDurationOptions() {
  return PAUSE_DURATION_MONTHS.map(months => ({
    value: months,
    label: months === 1 ? '1 Month' : `${months} Months`,
  }));
}

export function getDefaultPauseDurationMonths(membershipDetails) {
  const resumeAt = get(membershipDetails, 'resume_at');
  if (!resumeAt) {
    return 1;
  }
  const resume = dayjs.utc(String(resumeAt).replace(/-/g, '/'));
  if (!resume.isValid()) {
    return 1;
  }
  const months = Math.round(resume.diff(dayjs(), 'month', true));
  if (months < 1) {
    return 1;
  }
  if (months > 12) {
    return 12;
  }
  return months;
}

export function getPauseResumeAt(pauseMonths, fromDate = dayjs()) {
  return dayjs(fromDate).add(Number(pauseMonths) || 1, 'month');
}

export function formatPauseResumeAtForApi(pauseMonths, fromDate = dayjs()) {
  return getPauseResumeAt(pauseMonths, fromDate).format('YYYY-MM-DD HH:mm:ss');
}

export function getManagePauseIntroCopy(membershipDetails) {
  if (isMembershipTermsV02(membershipDetails)) {
    return 'You can pause today and keep your membership benefits, or cancel.';
  }
  return 'You can pause or cancel your membership at any time.';
}

export function getManagePauseOfferCopy(membershipDetails) {
  const fee = getPauseFeeAmount(membershipDetails);
  if (!shouldShowPauseFee(membershipDetails) || fee == null) {
    return null;
  }
  return `Pause today for $${fee} per month and keep all of your membership benefits, including member rates, without adding any more Squeeze Bucks to your account.`;
}

export function getPauseCtaLabel(membershipDetails, { isPaused } = {}) {
  if (isPaused) {
    return 'Update Pause Date';
  }
  const fee = getPauseFeeAmount(membershipDetails);
  if (shouldShowPauseFee(membershipDetails) && fee != null) {
    return `Pause for $${fee} /mo`;
  }
  return 'Pause Membership';
}

export function getMembershipMassageMinutes(membershipDetails) {
  const planId = get(membershipDetails, 'stripe_plan_id') || '';
  return String(planId).startsWith('50') ? 50 : 80;
}

/** Manage page / pre-cancel explainer — v01 ends on next credit date (no extra 30 days). */
export const V01_CANCELLATION_MANAGE_COPY =
  'You can cancel your membership at any time. Cancellation takes effect on your next member credit date.';

export const V01_CANCELLATION_CONFIRM_COPY =
  'Cancellation takes effect on your next member credit date.';

export const V02_CANCELLATION_MANAGE_COPY =
  'You can cancel your membership at any time. Cancellation takes effect 30 days after your next member credit date.';

export const V02_CANCELLATION_CONFIRM_COPY =
  'Cancellation takes effect 30 days after your next member credit date, and you will be charged one final membership payment before it ends. Your accrued Squeeze Bucks will remain in your account.';

export function formatMembershipTermsDate(dateValue) {
  if (!dateValue) {
    return '';
  }
  if (dayjs.isDayjs(dateValue)) {
    if (!dateValue.isValid()) {
      return '';
    }
    return dateValue.format(MEMBERSHIP_DISPLAY_DATE_FORMAT);
  }
  const parsed = dayjs.utc(String(dateValue).replace(/-/g, '/'));
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.local().format(MEMBERSHIP_DISPLAY_DATE_FORMAT);
}

/**
 * GX / guest "Reactivate" for a scheduled cancel (To be Cancelled).
 * API PUT action:resume → unCancel: clears cancel dates, does not charge.
 * Distinct from paused "Resume Now", which does charge.
 */
export function isUncancelReactivate(
  selectedMembershipType,
  membershipDetails,
) {
  if (get(selectedMembershipType, 'action') !== 'resume') {
    return false;
  }
  if (get(membershipDetails, 'paused_at')) {
    return false;
  }
  return !!(
    get(membershipDetails, 'cancelled_at') ||
    get(membershipDetails, 'cancel_effective_at')
  );
}

export function getCancellationEndAndBillDates(membershipDetails) {
  const nextDueAt = get(membershipDetails, 'next_due_at');
  let endDateSource = nextDueAt;

  if (isMembershipTermsV02(membershipDetails) && nextDueAt) {
    endDateSource = dayjs(String(nextDueAt).replace(/-/g, '/'))
      .add(30, 'days')
      .format('YYYY-MM-DD');
  }

  return {
    endDate: formatMembershipTermsDate(endDateSource),
    nextBillDate: formatMembershipTermsDate(nextDueAt),
  };
}

export function getCancellationConfirmParagraphs(membershipDetails) {
  const { endDate, nextBillDate } = getCancellationEndAndBillDates(
    membershipDetails,
  );

  if (isMembershipTermsV02(membershipDetails)) {
    return [
      `Cancel today and your 30d notice period will start. You will have one more payment on ${nextBillDate} and can continue enjoying your membership until ${endDate}.`,
      'Any unused Squeeze Bucks can be redeemed within 90 days at member rates and then they expire.',
    ];
  }
  return [
    `Cancel today and your membership will end on ${endDate}. You can continue enjoying your membership until that date.`,
    'Any unused Squeeze Bucks can be redeemed at regular rates but membership benefits do not apply.',
  ];
}

/** Forward-looking copy for the "Are you sure?" cancel confirmation page. */
export function getCancellationConfirmCopy(membershipDetails) {
  return getCancellationConfirmParagraphs(membershipDetails).join(' ');
}

/** Past-tense copy for the post-cancel confirmation page. */
export function getCancellationSuccessCopy(membershipDetails) {
  const { endDate, nextBillDate } = getCancellationEndAndBillDates(
    membershipDetails,
  );

  if (isMembershipTermsV02(membershipDetails)) {
    return `Thanks for being a Squeeze Member! We have accepted your 30 day notice to cancel. Just a friendly reminder that, per your Membership Agreement, your final membership payment is due on ${nextBillDate}. You'll continue enjoying your membership benefits through your membership end date of ${endDate}.`;
  }
  return `Thanks for being a Squeeze Member! Your membership has been canceled. You can continue enjoying your membership benefits through ${endDate}.`;
}
